import React, { useState, useEffect, useCallback } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { agentService } from '../../services/agentService';
import { isAdmin } from '../../services/auth';
import { useTranslation } from 'react-i18next';
import UserEditModal from './UserEditModal';
import UserAdjustmentModal from './UserAdjustmentModal';
import UserSupportModal from './UserSupportModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import toast from 'react-hot-toast';
import { countryCodes } from '../../utils/countryCodes';
import './UserDetails.css';
import './MajesticModal.css';
import { getAvatarSrc } from '../../utils/avatarUtils';

const currencyMap = {
    'None': '', 'USD': '$', 'GBP': '£', 'BTC': '₿', 'EUR': '€', 'CHF': '₣',
    'KRW': '₩', 'TRY': '₺', 'INR': '₹', 'CNY': '¥', 'THB': '฿',
    'RUB': '₽', 'UAH': '₴', 'BDT': '৳', 'JPY': '¥', 'ILS': '₪',
    'AED': 'د.إ', 'SAR': 'ر.س'
};

const UserDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showBalanceModal, setShowBalanceModal] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showTransactionPassword, setShowTransactionPassword] = useState(false);

    const fetchUser = useCallback(async () => {
        try {
            const response = await agentService.getUser(id);
            if (response.success) {
                setUser(response.user);
            }
        } catch (error) {
            toast.error(t('error_fetch_user_details') || 'Failed to fetch user details');
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const handleDelete = async () => {
        try {
            const response = await agentService.deleteUser(id);
            if (response.success) {
                toast.success(t('user_deleted_success') || 'User deleted successfully');
                setShowDeleteModal(false);
                navigate('/user-management');
            }
        } catch (error) {
            toast.error(t('error_delete_user') || 'Failed to delete user');
        }
    };

    if (loading) return <div className="user-details-container text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>;
    if (!user) return <div className="user-details-container text-center py-5">{t('no_data')}</div>;

    return (
        <div className="user-details-container animate-fade-in">
            {/* Header */}
            <div className="details-header">
                <button className="return-btn d-flex align-items-center" onClick={() => navigate('/user-management')}>
                    <i className="bi bi-arrow-left me-2"></i> {t('return')}
                </button>
                <span className="header-title">{t('user_details')}</span>
            </div>

            {/* Main Profile Card */}
            <div className="basic-info-card">
                <div className="avatar-section">
                    <div className="majestic-avatar-wrapper">
                        <div className="avatar-circle">
                            {user.profileImage ? (
                                <img
                                    src={getAvatarSrc(user.profileImage)}
                                    alt={user.username}
                                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', zIndex: 1, position: 'relative' }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        const container = e.target.parentElement;
                                        const icon = container.querySelector('.fallback-icon-i');
                                        if (icon) {
                                            icon.style.display = 'block';
                                            icon.style.opacity = '1';
                                        }
                                    }}
                                />
                            ) : null}
                            <i className="bi bi-person-fill fallback-icon-i"
                                style={{
                                    display: user.profileImage ? 'none' : 'block',
                                    fontSize: '2.5rem',
                                    color: 'rgba(255,255,255,0.2)',
                                    opacity: '1'
                                }}></i>
                        </div>
                    </div>

                    <h2 className="user-username">{user.username}</h2>
                    <div className="majestic-id-badge">ID: {user.id.slice(-8).toUpperCase()}</div>

                    <div className="balance-info">
                        <div className="balance-item">
                            <small>{t('balance')}</small>
                            <span>
                                {(user.currency && currencyMap[user.currency] !== undefined) ? currencyMap[user.currency] : (user.currency === 'None' ? '' : '$')}
                                {user.accountBalance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                        <div className="balance-item">
                            <small>{t('frozen')}</small>
                            <span>
                                {(user.currency && currencyMap[user.currency] !== undefined) ? currencyMap[user.currency] : (user.currency === 'None' ? '' : '$')}
                                {(user.frozenBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>

                    <div className="action-buttons-group">
                        <button className="action-btn" onClick={() => setShowEditModal(true)}>
                            <i className="bi bi-pencil-square"></i> {t('edit')}
                        </button>
                        <button className="action-btn" onClick={() => setShowDeleteModal(true)}>
                            <i className="bi bi-trash"></i> {t('delete')}
                        </button>
                        {isAdmin() && (
                            <>
                                <button className="action-btn" onClick={() => setShowBalanceModal(true)}>
                                    <i className="bi bi-wallet2"></i> {t('control')}
                                </button>
                                <button className="action-btn" onClick={() => setShowSupportModal(true)}>
                                    <i className="bi bi-headset"></i> {t('support')}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Info Sections */}
                <div className="section-header">
                    <span>{t('user_basic_info') || 'Detailed Information'}</span>
                </div>

                <div className="info-grid">
                    <div className="info-item">
                        <span className="info-label">{t('account')}</span>
                        <span className="info-value">{user.username}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">{t('email')}</span>
                        <span className="info-value">{user.email || '-'}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">{t('mobile_number')}</span>
                        <span className="info-value">
                            {(() => {
                                if (!user.mobileNumber) return '-';
                                const matchedCode = countryCodes.find(c => user.mobileNumber.startsWith(c.code));
                                if (matchedCode) {
                                    const number = user.mobileNumber.replace(matchedCode.code, '');
                                    return `${matchedCode.flag} ${matchedCode.code} ${number}`;
                                }
                                return user.mobileNumber;
                            })()}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">{t('password')}</span>
                        <div className="d-flex align-items-center justify-content-between">
                            <span className="info-value">
                                {showPassword ? (user.password || '********') : '********'}
                            </span>
                            <button className="btn btn-link p-0 text-secondary border-0 shadow-none" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </button>
                        </div>
                    </div>
                    <div className="info-item">
                        <span className="info-label">{t('user_transaction')}</span>
                        <div className="d-flex align-items-center justify-content-between">
                            <span className="info-value">
                                {showTransactionPassword ? (user.transactionPassword || '********') : '********'}
                            </span>
                            <button className="btn btn-link p-0 text-secondary border-0 shadow-none" onClick={() => setShowTransactionPassword(!showTransactionPassword)}>
                                {showTransactionPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </button>
                        </div>
                    </div>
                    <div className="info-item">
                        <span className="info-label">{t('vip_level')}</span>
                        <span className="info-value">{user.vipLevel || 0}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">{t('credit_score')}</span>
                        <span className="info-value">{user.creditScore ?? 0}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">{t('wallet_address')}</span>
                        <span className="info-value" style={{ wordBreak: 'break-all' }}>{user.walletAddress || '-'}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">{t('state')}</span>
                        <span className="info-value state-tag">{user.isActive ? t('normal') : t('forbidden')}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">{t('registration_time')}</span>
                        <span className="info-value">{new Date(user.createdAt).toLocaleString('en-GB', { hour12: false })}</span>
                    </div>
                </div>
            </div>

            {showEditModal && (
                <UserEditModal
                    show={showEditModal}
                    onHide={() => setShowEditModal(false)}
                    user={user}
                    onSuccess={fetchUser}
                />
            )}

            {showBalanceModal && (
                <UserAdjustmentModal
                    show={showBalanceModal}
                    onHide={() => setShowBalanceModal(false)}
                    user={user}
                    onSuccess={fetchUser}
                />
            )}

            {showSupportModal && (
                <UserSupportModal
                    show={showSupportModal}
                    onHide={() => setShowSupportModal(false)}
                    user={user}
                    onSuccess={fetchUser}
                />
            )}

            {showDeleteModal && (
                <DeleteConfirmationModal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    onConfirm={handleDelete}
                    username={user.username}
                />
            )}
        </div>
    );
};

export default UserDetails;
